import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import DateRangePicker from "components/date-range-picker";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { toast } from "react-toastify";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import useHistoryPanel from "hooks/shared/useHistoryPanel";
import { useAppTranslation } from "locale/useAppTranslation";
import { errorHandler } from "_services/errorHandler";
import { DurationCondition } from "_services/modules/waitingTimeRate";
import { editWaitingTimeRatePageRouteName } from "../edit/edit.page";
import {
  useWaitingTimeServiceV1ListWaitingTimeRatesQuery,
  useWaitingTimeServiceV1ActivateWaitingTimeRatesMutation,
  useWaitingTimeServiceV1DeactivateWaitingTimeRatesMutation,
  type WaitingTimeV1WaitingTimeRateRes,
} from "_services/modules/waiting_time";

const sortFieldMap: Record<string, string> = {
  waitingTimeRateName: "waiting_time_rate_name",
  durationCondition: "duration_condition",
  status: "status",
  createdAt: "created_at",
  updatedAt: "updated_at",
};

export interface WaitingTimeRateRow {
  id: string;
  waitingTimeRateName: string;
  durationCondition: DurationCondition;
  durationMin: number | null;
  durationMax: number | null;
  rate: number;
  description: string;
  status: "active" | "inactive";
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

const formatDuration = (row: WaitingTimeRateRow, hoursUnit: string): string => {
  if (row.durationCondition === "below") {
    return `< ${row.durationMax} ${hoursUnit}`;
  }
  if (row.durationCondition === "between") {
    return `${row.durationMin} ${hoursUnit} - ${row.durationMax} ${hoursUnit}`;
  }
  if (row.durationCondition === "above") {
    return `> ${row.durationMin} ${hoursUnit}`;
  }
  return "-";
};

const formatRate = (rate: number): string => {
  return `Rp ${new Intl.NumberFormat("id-ID").format(rate)}`;
};

const useWaitingTimeRateList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("waitingTimeRate");
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const [toggleRow, setToggleRow] = useState<WaitingTimeRateRow | null>(null);
  const historyPanel = useHistoryPanel("waiting_time_rate");

  const { data: waitingTimeRatesData, isLoading, refetch } = useWaitingTimeServiceV1ListWaitingTimeRatesQuery({
    page: currentPage,
    size: rowsPerPage,
    sort: sortState?.columnId ? (sortFieldMap[sortState.columnId] ?? sortState.columnId) : "created_at",
    order: sortState?.direction ?? "desc",
    status: filterValues.status || undefined,
    waitingTimeRateName: filterValues.waitingTimeRateName || undefined,
    duration: filterValues.durationCondition || undefined,
    rate: filterValues.rate || undefined,
    createdAtFrom: filterValues.createdAt?.split("|")[0] || undefined,
    createdAtTo: filterValues.createdAt?.split("|")[1] || undefined,
    updatedAtFrom: filterValues.updatedAt?.split("|")[0] || undefined,
    updatedAtTo: filterValues.updatedAt?.split("|")[1] || undefined,
  });
  const [activateWaitingTimeRates] = useWaitingTimeServiceV1ActivateWaitingTimeRatesMutation();
  const [deactivateWaitingTimeRates] = useWaitingTimeServiceV1DeactivateWaitingTimeRatesMutation();

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-list"),
        hasBackButton: false,
      })
    );
  }, [dispatch, t]);

  const tableData: WaitingTimeRateRow[] = useMemo(() => {
    if (!waitingTimeRatesData?.waiting_time_rates) return [];
    return waitingTimeRatesData.waiting_time_rates.map((item: WaitingTimeV1WaitingTimeRateRes) => ({
      id: item.id || "",
      waitingTimeRateName: item.waiting_time_rate_name || "",
      durationCondition: (item.duration_condition || "below") as DurationCondition,
      durationMin: item.duration_min ?? null,
      durationMax: item.duration_max ?? null,
      rate: item.rate || 0,
      description: "",
      status: (item.status || "inactive") as "active" | "inactive",
      createdBy: item.created_by || "",
      createdAt: item.created_at || "",
      updatedBy: item.updated_by || "",
      updatedAt: item.updated_at || "",
    }));
  }, [waitingTimeRatesData]);

  const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
  const hasMixedStatus =
    selectedRows.length > 0 &&
    new Set(selectedRows.map((r) => r.status)).size > 1;

  const isBulkActionDisabled =
    selectedKeys.length === 0 || hasMixedStatus;

  const handleApplyBulkAction = () => {
    if (!bulkAction || isBulkActionDisabled) return;
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    try {
      const ids = selectedKeys.map((key) => String(key));
      if (bulkAction === "activate") {
        await activateWaitingTimeRates({ waitingTimeV1WaitingTimeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      } else if (bulkAction === "deactivate") {
        await deactivateWaitingTimeRates({ waitingTimeV1WaitingTimeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      }
      setShowBulkConfirmModal(false);
      setBulkAction("");
      setSelectedKeys([]);
      refetch();
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorHandler(error);
    }
  };

  const handleToggleStatus = (row: WaitingTimeRateRow) => {
    setToggleRow(row);
    setShowToggleConfirmModal(true);
  };

  const handleConfirmToggle = async () => {
    if (!toggleRow) return;
    try {
      const ids = [toggleRow.id];
      if (toggleRow.status === "active") {
        await deactivateWaitingTimeRates({ waitingTimeV1WaitingTimeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      } else {
        await activateWaitingTimeRates({ waitingTimeV1WaitingTimeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      }
      setShowToggleConfirmModal(false);
      setToggleRow(null);
      refetch();
    } catch (error) {
      console.error("Toggle failed:", error);
      errorHandler(error);
    }
  };

  const handleFilterChange = (values: Record<string, string>) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleEdit = (row: WaitingTimeRateRow) => {
    navigate(editWaitingTimeRatePageRouteName(row.id.toString()));
  };

  const columns: Columns<WaitingTimeRateRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
      },
      {
        id: "waitingTimeRateName",
        fieldId: "waitingTimeRateName",
        label: t("label-waiting-time-rate-name"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "durationCondition",
        fieldId: "durationCondition",
        label: t("label-duration-condition"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-duration-condition-${row.id}`}>{formatDuration(row, t("col-unit-hours"))}</span>
        ),
      },
      {
        id: "rate",
        fieldId: "rate",
        label: t("label-rate"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-rate-${row.id}`}>{formatRate(row.rate)}</span>
        ),
      },
      {
        id: "status",
        fieldId: "status",
        label: t("label-status"),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t("col-all-data"), value: "" },
          { label: t("status-active"), value: "active" },
          { label: t("status-inactive"), value: "inactive" },
        ],
        render: (row) => (
          <div className="inline-flex items-center gap-2 text-sm" data-testid={`col-status-${row.id}`}>
            <span
              className={`h-3 w-3 rounded-full ${
                row.status === "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
              }`}
            />
            <span className="capitalize">{row.status}</span>
          </div>
        ),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "140px",
        dragDisabled: true,
        render: (row) => (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => handleEdit(row)}
              data-testid="btn-edit-waiting-time-rate"
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label: row.status === "active" ? t("btn-deactivate") : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                  variant: row.status === "active" ? "danger" : "default",
                  testId: row.status === "active" ? "btn-deactivate-waiting-time-rate" : "btn-activate-waiting-time-rate",
                },
                {
                  label: t("btn-track-history"),
                  onClick: () => historyPanel.openPanel(row.id, row.waitingTimeRateName),
                  testId: "btn-track-history-waiting-time-rate",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  const totalPages = waitingTimeRatesData?.metadata?.total_page ?? 1;

  return {
    tableData,
    isLoading,
    data: tableData,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    handleFilterChange,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleConfirmToggle,
    historyPanel,
    t,
  };
};

export default useWaitingTimeRateList;
